// A small, JSON-serialisable condition DSL used to gate flow stages on the
// onboarding criteria. Serialisable on purpose: a policy (including these
// conditions) can later live in a database and be edited from the back office
// without shipping code.

import { z } from 'zod';

import type { AccountType, OnboardingCriteria } from './types.js';

export type FlowCondition =
  | { all: FlowCondition[] }
  | { any: FlowCondition[] }
  | { not: FlowCondition }
  | { accountType: AccountType }
  | { countryIn: string[] }
  | { jurisdiction: string }
  | { always: true };

export const flowConditionSchema: z.ZodType<FlowCondition> = z.lazy(() =>
  z.union([
    z.object({ all: z.array(flowConditionSchema) }),
    z.object({ any: z.array(flowConditionSchema) }),
    z.object({ not: flowConditionSchema }),
    z.object({ accountType: z.enum(['PERSONAL', 'BUSINESS']) }),
    z.object({ countryIn: z.array(z.string()) }),
    z.object({ jurisdiction: z.string() }),
    z.object({ always: z.literal(true) }),
  ]),
);

export function evaluateCondition(condition: FlowCondition, criteria: OnboardingCriteria): boolean {
  if ('always' in condition) return condition.always;
  if ('all' in condition) return condition.all.every((c) => evaluateCondition(c, criteria));
  if ('any' in condition) return condition.any.some((c) => evaluateCondition(c, criteria));
  if ('not' in condition) return !evaluateCondition(condition.not, criteria);
  if ('accountType' in condition) return condition.accountType === criteria.accountType;
  if ('countryIn' in condition) return condition.countryIn.includes(criteria.country);
  if ('jurisdiction' in condition) return condition.jurisdiction === criteria.jurisdiction;
  return false;
}
